import React, { useState, useEffect, useContext } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  CircularProgress,
  makeStyles,
  useTheme,
  Button,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip
} from "@material-ui/core";
import {
  SyncAlt as SyncAltIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Business as BusinessIcon,
  Storage as StorageIcon,
  Settings as SettingsIcon
} from "@material-ui/icons";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  AreaChart,
  Area
} from "recharts";
import api from "../../services/api";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1),
    },
  },
  card: {
    marginBottom: theme.spacing(2),
    borderRadius: 12,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.mode === 'dark' 
      ? '0 2px 8px rgba(0,0,0,0.3)' 
      : '0 2px 8px rgba(0,0,0,0.1)',
    transition: 'all 0.3s ease',
    '&:hover': {
      boxShadow: theme.mode === 'dark' 
        ? '0 4px 16px rgba(0,0,0,0.5)' 
        : '0 4px 16px rgba(0,0,0,0.15)',
    },
  },
  cardHeader: {
    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    color: 'white',
    padding: theme.spacing(2),
    borderRadius: '12px 12px 0 0',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  cardContent: {
    padding: theme.spacing(2),
  },
  metricBox: {
    padding: theme.spacing(2),
    borderRadius: 12,
    backgroundColor: theme.mode === 'dark' ? '#374151' : '#f8f9fa',
    border: `2px solid ${theme.mode === 'dark' ? '#4B5563' : '#e9ecef'}`,
    marginBottom: theme.spacing(2),
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: theme.mode === 'dark' ? '#4B5563' : '#e3f2fd',
      transform: 'translateY(-2px)',
    },
  },
  metricLabel: {
    fontSize: '12px',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
    fontWeight: 500,
  },
  metricValue: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: theme.palette.text.primary,
  },
  metricUnit: {
    fontSize: '14px',
    color: theme.palette.text.secondary,
    marginLeft: theme.spacing(0.5),
  },
  progressBar: {
    marginTop: theme.spacing(1),
    height: 8,
    borderRadius: 4,
    backgroundColor: theme.mode === 'dark' ? '#374151' : '#e5e7eb',
    '& .MuiLinearProgress-bar': {
      borderRadius: 4,
    },
  },
  chartContainer: {
    marginTop: theme.spacing(2),
    height: 300,
    [theme.breakpoints.down('sm')]: {
      height: 250,
    },
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 400,
  },
  alertCard: {
    padding: theme.spacing(2),
    borderRadius: 8,
    marginBottom: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  alertInfo: {
    backgroundColor: theme.mode === 'dark' ? '#1e3a8a' : '#dbeafe',
    border: `2px solid ${theme.mode === 'dark' ? '#3b82f6' : '#60a5fa'}`,
    color: theme.mode === 'dark' ? '#93c5fd' : '#1e40af',
  },
  alertWarning: {
    backgroundColor: theme.mode === 'dark' ? '#78350f' : '#fef3c7',
    border: `2px solid ${theme.mode === 'dark' ? '#f59e0b' : '#fbbf24'}`,
    color: theme.mode === 'dark' ? '#fcd34d' : '#92400e',
  },
  alertError: {
    backgroundColor: theme.mode === 'dark' ? '#7f1d1d' : '#fee2e2',
    border: `2px solid ${theme.mode === 'dark' ? '#dc2626' : '#ef4444'}`,
    color: theme.mode === 'dark' ? '#fca5a5' : '#991b1b',
  },
  alertCritical: {
    backgroundColor: theme.mode === 'dark' ? '#450a0a' : '#fee2e2',
    border: `2px solid ${theme.mode === 'dark' ? '#dc2626' : '#ef4444'}`,
    color: theme.mode === 'dark' ? '#fca5a5' : '#991b1b',
  },
  table: {
    minWidth: 650,
  },
  tableRow: {
    '&:hover': {
      backgroundColor: theme.mode === 'dark' ? '#374151' : '#f5f5f5',
    },
  },
}));

const getStatusColor = (percentage) => {
  if (percentage < 50) return '#10B981'; // Verde
  if (percentage < 80) return '#F59E0B'; // Amarillo
  if (percentage < 95) return '#EF4444'; // Rojo
  return '#DC2626'; // Rojo oscuro
};

const getStatusText = (status) => {
  switch (status) {
    case 'optimal': return 'Óptimo';
    case 'warning': return 'Advertencia';
    case 'error': return 'Error';
    case 'critical': return 'Crítico';
    default: return 'Desconocido';
  }
};

const getAlertIcon = (level) => {
  switch (level) {
    case 'info':
      return <InfoIcon />;
    case 'warning':
      return <WarningIcon />;
    case 'error':
      return <ErrorIcon />;
    case 'critical':
      return <ErrorIcon />;
    default:
      return <InfoIcon />;
  }
};

const ConnectionsDashboard = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { user } = useContext(AuthContext);
  
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [utilizationHistory, setUtilizationHistory] = useState([]);
  const [thresholdsDialogOpen, setThresholdsDialogOpen] = useState(false);
  const [thresholds, setThresholds] = useState({
    thresholdInfo: 20,
    thresholdWarning: 40,
    thresholdError: 60,
    thresholdError2: 80,
    thresholdCritical: 95
  });

  // ✅ Cargar datos del dashboard
  const loadDashboardData = async () => {
    try {
      setError(null);
      const response = await api.get('/dashboard/connections');
      
      if (response.data?.success && response.data?.data) {
        const data = response.data.data;
        setDashboardData(data);
        setThresholds(data.thresholds);

        // Agregar al historial de utilización
        setUtilizationHistory(prev => {
          const newEntry = {
            time: new Date().toLocaleTimeString(),
            utilization: data.global.utilization,
            sessions: data.global.activeSessions,
            maxTheoretical: data.global.maxTheoretical
          };
          return [...prev.slice(-19), newEntry]; // Mantener últimas 20 muestras
        });
      }
    } catch (err) {
      console.error('Error cargando dashboard:', err);
      setError('Error al cargar datos del dashboard');
      toast.error('Error al cargar datos del dashboard');
    } finally {
      setLoading(false);
    }
  };

  // ✅ Guardar umbrales
  const saveThresholds = async () => {
    try {
      await api.put('/dashboard/connections/thresholds', thresholds);
      toast.success('Umbrales actualizados correctamente');
      setThresholdsDialogOpen(false);
      loadDashboardData();
    } catch (err) {
      console.error('Error guardando umbrales:', err);
      toast.error('Error al guardar umbrales');
    }
  };

  // ✅ Cargar datos inicialmente y cada 5 segundos
  useEffect(() => {
    loadDashboardData();
    const interval = setInterval(loadDashboardData, 5000);
    
    return () => clearInterval(interval);
  }, []);

  if (loading && !dashboardData) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
        <Typography variant="body1" style={{ marginLeft: 16 }}>
          Cargando dashboard de conexiones...
        </Typography>
      </Box>
    );
  }

  if (error && !dashboardData) {
    return (
      <Box className={classes.loadingContainer}>
        <Typography variant="h6" color="error">{error}</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={loadDashboardData}
          style={{ marginTop: 16 }}
        >
          Reintentar
        </Button>
      </Box>
    );
  }

  if (!dashboardData) {
    return (
      <Box className={classes.container}>
        <Typography variant="h6">No hay datos disponibles</Typography>
      </Box>
    );
  }

  const { global, byCompany, topCompanies, alerts, dbTableMetrics } = dashboardData;

  return (
    <Box className={classes.container}>
      {/* ✅ SECCIÓN 1: RESUMEN EJECUTIVO */}
      <Card className={classes.card}>
        <Box className={classes.cardHeader}>
          <SyncAltIcon />
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            <span role="img" aria-label="Gráfico de barras">📊</span> Resumen Global de Sesiones WhatsApp
          </Typography>
          <Box marginLeft="auto" display="flex" gap={1}>
            <Button
              size="small"
              startIcon={<SettingsIcon />}
              onClick={() => setThresholdsDialogOpen(true)}
              variant="outlined"
              style={{ 
                backgroundColor: 'rgba(255,255,255,0.2)',
                color: 'white',
                borderColor: 'rgba(255,255,255,0.3)'
              }}
            >
              Umbrales
            </Button>
            <Button
              size="small"
              startIcon={<RefreshIcon />}
              onClick={loadDashboardData}
              variant="outlined"
              style={{ 
                backgroundColor: 'rgba(255,255,255,0.2)',
                color: 'white',
                borderColor: 'rgba(255,255,255,0.3)'
              }}
            >
              Actualizar
            </Button>
          </Box>
        </Box>
        <CardContent className={classes.cardContent}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Tooltip 
                title="Número total de conexiones WhatsApp activas en este momento en todo el sistema. Cada conexión representa un número de WhatsApp conectado y funcionando."
                arrow
                placement="top"
              >
                <Box className={classes.metricBox} style={{ cursor: 'help' }}>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography className={classes.metricLabel}>Sesiones Activas</Typography>
                    <InfoIcon style={{ fontSize: 16, opacity: 0.6 }} />
                  </Box>
                  <Typography className={classes.metricValue}>
                    {global.activeSessions}
                  </Typography>
                  <Typography variant="caption" color="textSecondary" style={{ marginTop: 4, display: 'block' }}>
                    De {global.maxTheoretical} capacidad máxima
                  </Typography>
                  <Typography variant="caption" color="textSecondary" style={{ fontSize: '0.7rem', fontStyle: 'italic' }}>
                    Conexiones WhatsApp en uso
                  </Typography>
                </Box>
              </Tooltip>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Tooltip 
                title="Capacidad máxima teórica del servidor. Se calcula sumando el límite de conexiones de todos los planes activos de todas las empresas. Este es el máximo de conexiones que el servidor puede soportar según las licencias contratadas."
                arrow
                placement="top"
              >
                <Box className={classes.metricBox} style={{ cursor: 'help' }}>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography className={classes.metricLabel}>Capacidad Teórica</Typography>
                    <InfoIcon style={{ fontSize: 16, opacity: 0.6 }} />
                  </Box>
                  <Typography className={classes.metricValue}>
                    {global.maxTheoretical}
                  </Typography>
                  <Typography variant="caption" color="textSecondary" style={{ marginTop: 4, display: 'block' }}>
                    Suma de todos los planes
                  </Typography>
                  <Typography variant="caption" color="textSecondary" style={{ fontSize: '0.7rem', fontStyle: 'italic' }}>
                    Límite según licencias
                  </Typography>
                </Box>
              </Tooltip>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Tooltip 
                title="Porcentaje de utilización de la capacidad del servidor. Indica qué tan cerca estamos del límite máximo. Verde (0-40%): Normal. Amarillo (40-80%): Atención. Rojo (80-95%): Crítico. El servidor puede bloquear nuevas conexiones si supera el 95%."
                arrow
                placement="top"
              >
                <Box className={classes.metricBox} style={{ cursor: 'help' }}>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography className={classes.metricLabel}>Utilización</Typography>
                    <InfoIcon style={{ fontSize: 16, opacity: 0.6 }} />
                  </Box>
                  <Typography className={classes.metricValue} style={{ color: getStatusColor(global.utilization) }}>
                    {global.utilization.toFixed(1)}
                    <span className={classes.metricUnit}>%</span>
                  </Typography>
                  <Box style={{ position: 'relative', marginTop: 8 }}>
                    <LinearProgress
                      variant="determinate"
                      value={100}
                      className={classes.progressBar}
                    />
                    <Box
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        height: 8,
                        width: `${Math.min(global.utilization, 100)}%`,
                        backgroundColor: getStatusColor(global.utilization),
                        borderRadius: 4,
                        transition: 'width 0.3s ease',
                      }}
                    />
                  </Box>
                  <Typography variant="caption" color="textSecondary" style={{ fontSize: '0.7rem', fontStyle: 'italic', marginTop: 4, display: 'block' }}>
                    {global.utilization < 40 ? 'Uso normal' : global.utilization < 80 ? 'Requiere atención' : 'Nivel crítico'}
                  </Typography>
                </Box>
              </Tooltip>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Tooltip 
                title="Estado general del sistema de conexiones. Óptimo: Todo funciona bien. Advertencia: Se está acercando al límite. Error: Nivel alto de uso. Crítico: El servidor puede bloquear nuevas conexiones."
                arrow
                placement="top"
              >
                <Box className={classes.metricBox} style={{ cursor: 'help' }}>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography className={classes.metricLabel}>Estado</Typography>
                    <InfoIcon style={{ fontSize: 16, opacity: 0.6 }} />
                  </Box>
                  <Box display="flex" alignItems="center" gap={1} marginTop={1}>
                    {global.status === 'optimal' ? (
                      <CheckCircleIcon style={{ color: '#10B981', fontSize: 32 }} />
                    ) : global.status === 'warning' ? (
                      <WarningIcon style={{ color: '#F59E0B', fontSize: 32 }} />
                    ) : (
                      <ErrorIcon style={{ color: '#EF4444', fontSize: 32 }} />
                    )}
                    <Typography variant="h6" style={{ color: getStatusColor(global.utilization) }}>
                      {getStatusText(global.status)}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="textSecondary" style={{ marginTop: 4, display: 'block' }}>
                    {global.activeCompanies} empresas activas
                  </Typography>
                  <Typography variant="caption" color="textSecondary" style={{ fontSize: '0.7rem', fontStyle: 'italic' }}>
                    {global.status === 'optimal' ? 'Sistema estable' : global.status === 'warning' ? 'Monitorear uso' : 'Acción requerida'}
                  </Typography>
                </Box>
              </Tooltip>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* ✅ SECCIÓN 2: DESGLOSE POR EMPRESA */}
      {byCompany.length > 0 && (
        <Card className={classes.card}>
          <Box className={classes.cardHeader}>
            <BusinessIcon />
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              <span role="img" aria-label="Edificio">🏢</span> Sesiones por Empresa
            </Typography>
          </Box>
          <CardContent className={classes.cardContent}>
            <TableContainer component={Paper} style={{ backgroundColor: 'transparent', boxShadow: 'none' }}>
              <Table className={classes.table}>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Empresa</strong></TableCell>
                    <TableCell><strong>Plan</strong></TableCell>
                    <TableCell align="right"><strong>Sesiones Usadas</strong></TableCell>
                    <TableCell align="right"><strong>Límite Plan</strong></TableCell>
                    <TableCell align="right"><strong>Utilización</strong></TableCell>
                    <TableCell align="center"><strong>Estado</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {byCompany.map((company) => (
                    <TableRow key={company.companyId} className={classes.tableRow}>
                      <TableCell>{company.companyName}</TableCell>
                      <TableCell>{company.planName}</TableCell>
                      <TableCell align="right">{company.activeSessions}</TableCell>
                      <TableCell align="right">{company.planLimit}</TableCell>
                      <TableCell align="right">
                        <Typography style={{ color: getStatusColor(company.utilization), fontWeight: 'bold' }}>
                          {company.utilization.toFixed(1)}%
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={company.utilization < 50 ? 'Óptimo' : company.utilization < 80 ? 'Advertencia' : 'Crítico'}
                          size="small"
                          style={{
                            backgroundColor: getStatusColor(company.utilization),
                            color: 'white'
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* ✅ SECCIÓN 3: TOP 5 EMPRESAS CON MAYOR CONSUMO */}
      {topCompanies.length > 0 && (
        <Card className={classes.card}>
          <Box className={classes.cardHeader}>
            <TrendingUpIcon />
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              <span role="img" aria-label="Fuego">🔥</span> Top 5 Empresas con Mayor Consumo
            </Typography>
          </Box>
          <CardContent className={classes.cardContent}>
            <Grid container spacing={2}>
              {topCompanies.map((company, index) => (
                <Grid item xs={12} sm={6} md={4} key={company.companyId}>
                  <Box className={classes.metricBox}>
                    <Typography className={classes.metricLabel}>
                      #{index + 1} {company.companyName}
                    </Typography>
                    <Typography className={classes.metricValue} style={{ fontSize: 24, color: getStatusColor(company.utilization) }}>
                      {company.activeSessions}/{company.planLimit}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {company.utilization.toFixed(1)}% de su plan ({company.planName})
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* ✅ SECCIÓN 4: GRÁFICO DE UTILIZACIÓN */}
      {utilizationHistory.length > 0 && (
        <Card className={classes.card}>
          <Box className={classes.cardHeader}>
            <TrendingUpIcon />
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="h6" style={{ fontWeight: 600 }}>
                  <span role="img" aria-label="Gráfico de tendencia">📈</span> Historial de Utilización
                </Typography>
                <Tooltip 
                  title="Gráfico que muestra la evolución del porcentaje de utilización del servidor en las últimas 20 mediciones (cada 5 segundos). Útil para detectar tendencias y picos de uso."
                  arrow
                >
                  <InfoIcon style={{ fontSize: 18, opacity: 0.7, cursor: 'help' }} />
                </Tooltip>
                <Typography variant="caption" color="textSecondary" style={{ marginLeft: 'auto' }}>
                  Últimas 20 muestras
                </Typography>
              </Box>
            </Typography>
          </Box>
          <CardContent className={classes.cardContent}>
            <Box className={classes.chartContainer}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={utilizationHistory}>
                  <defs>
                    <linearGradient id="colorUtilization" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                  <XAxis dataKey="time" stroke={theme.palette.text.secondary} />
                  <YAxis stroke={theme.palette.text.secondary} domain={[0, 100]} />
                  <Tooltip 
                    formatter={(value) => `${value.toFixed(1)}%`}
                    contentStyle={{
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.divider}`
                    }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="utilization" 
                    stroke="#3B82F6" 
                    fillOpacity={1} 
                    fill="url(#colorUtilization)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* ✅ SECCIÓN 4.5: INFORMACIÓN SOBRE RECURSOS DEL SERVIDOR */}
      <Card className={classes.card}>
        <Box className={classes.cardHeader}>
          <InfoIcon />
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            <span role="img" aria-label="Información">ℹ️</span> Recursos del Servidor
          </Typography>
        </Box>
        <CardContent className={classes.cardContent}>
          <Box 
            style={{ 
              padding: 16, 
              borderRadius: 8, 
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.05)',
              border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.2)'}`
            }}
          >
            <Typography variant="body2" style={{ marginBottom: 12, fontWeight: 600, color: '#3B82F6' }}>
              Consumo de Recursos por Sesión WhatsApp
            </Typography>
            
            <Box style={{ marginBottom: 16 }}>
              <Typography variant="body2" style={{ marginBottom: 8 }}>
                <strong>Cada sesión WhatsApp consume aproximadamente:</strong>
              </Typography>
              <Box style={{ marginLeft: 16, marginBottom: 8 }}>
                <Typography variant="body2" color="textSecondary">
                  • <strong>RAM:</strong> ~400 MB por sesión
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  • <strong>CPU:</strong> ~1.5% por sesión
                </Typography>
              </Box>
            </Box>

            <Box style={{ marginBottom: 16, padding: 12, backgroundColor: theme.palette.mode === 'dark' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(16, 185, 129, 0.05)', borderRadius: 6 }}>
              <Typography variant="body2" style={{ marginBottom: 8, fontWeight: 600, color: '#10B981' }}>
                Ejemplo para 20 conexiones:
              </Typography>
              <Box style={{ marginLeft: 16 }}>
                <Typography variant="body2" color="textSecondary">
                  • <strong>RAM:</strong> ~8 GB
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  • <strong>CPU:</strong> ~30%
                </Typography>
              </Box>
            </Box>

            <Box style={{ padding: 12, backgroundColor: theme.palette.mode === 'dark' ? 'rgba(245, 158, 11, 0.1)' : 'rgba(245, 158, 11, 0.05)', borderRadius: 6, border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(245, 158, 11, 0.3)' : 'rgba(245, 158, 11, 0.2)'}` }}>
              <Typography variant="body2" style={{ fontWeight: 600, color: '#F59E0B', marginBottom: 4 }}>
                <span role="img" aria-label="Recomendación">💡</span> Recomendación:
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginLeft: 20 }}>
                Servidor con mínimo <strong>16 GB RAM</strong> y <strong>CPU de 4+ núcleos</strong> para un rendimiento óptimo con múltiples conexiones.
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* ✅ SECCIÓN 5: HISTORIAL DE ALERTAS */}
      {alerts.length > 0 && (
        <Card className={classes.card}>
          <Box className={classes.cardHeader}>
            <WarningIcon />
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              <span role="img" aria-label="Advertencia">⚠️</span> Historial de Alertas (Últimas 24 horas) - {alerts.length} alertas
            </Typography>
          </Box>
          <CardContent className={classes.cardContent}>
            {alerts.slice(0, 10).map((alert) => (
              <Box
                key={alert.id}
                className={`${classes.alertCard} ${classes[`alert${alert.level.charAt(0).toUpperCase() + alert.level.slice(1)}`]}`}
              >
                {getAlertIcon(alert.level)}
                <Box flex={1}>
                  <Typography variant="body2" style={{ fontWeight: 600 }}>
                    [{alert.level.toUpperCase()}] {alert.message}
                  </Typography>
                  <Typography variant="caption" style={{ display: 'block', marginTop: 4 }}>
                    Sesiones: {alert.currentSessions}/{alert.maxTheoretical} ({alert.utilizationPercent.toFixed(1)}%)
                    {alert.impact && (
                      <> • Impacto: ~{(alert.impact.memoryMB / 1024).toFixed(1)} GB RAM, ~{alert.impact.cpuPercent.toFixed(1)}% CPU</>
                    )}
                    {alert.companyName && <> • Empresa: {alert.companyName}</>}
                  </Typography>
                  {alert.recommendation && (
                    <Typography variant="caption" style={{ display: 'block', marginTop: 4, fontStyle: 'italic' }}>
                      <span role="img" aria-label="Bombilla">💡</span> {alert.recommendation}
                    </Typography>
                  )}
                </Box>
                <Typography variant="caption">
                  {new Date(alert.createdAt).toLocaleString()}
                </Typography>
              </Box>
            ))}
          </CardContent>
        </Card>
      )}

      {/* ✅ SECCIÓN 6: TABLAS MÁS CRÍTICAS DE LA DB (Solo Super Admin) */}
      {user?.super && dbTableMetrics.length > 0 && (
        <Card className={classes.card}>
          <Box className={classes.cardHeader}>
            <StorageIcon />
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              <span role="img" aria-label="Gráfico de barras">📊</span> Tablas Más Críticas de la Base de Datos
            </Typography>
          </Box>
          <CardContent className={classes.cardContent}>
            <TableContainer component={Paper} style={{ backgroundColor: 'transparent', boxShadow: 'none' }}>
              <Table className={classes.table}>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Tabla</strong></TableCell>
                    <TableCell align="right"><strong>Filas</strong></TableCell>
                    <TableCell align="right"><strong>Tamaño (MB)</strong></TableCell>
                    <TableCell align="center"><strong>Estado</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dbTableMetrics.map((table) => (
                    <TableRow key={table.tableName} className={classes.tableRow}>
                      <TableCell>{table.tableName}</TableCell>
                      <TableCell align="right">{table.rowCount.toLocaleString()}</TableCell>
                      <TableCell align="right">{table.sizeMB.toFixed(2)} MB</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={table.status === 'critical' ? 'Crítico' : table.status === 'warning' ? 'Advertencia' : 'Normal'}
                          size="small"
                          style={{
                            backgroundColor: table.status === 'critical' ? '#EF4444' : table.status === 'warning' ? '#F59E0B' : '#10B981',
                            color: 'white'
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* ✅ DIALOG: CONFIGURACIÓN DE UMBRALES */}
      <Dialog open={thresholdsDialogOpen} onClose={() => setThresholdsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <SettingsIcon />
            <Typography variant="h6"><span role="img" aria-label="Engranaje">⚙️</span> Configuración de Umbrales de Alertas</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} marginTop={2}>
            <TextField
              label="Umbral Info (%)"
              type="number"
              value={thresholds.thresholdInfo}
              onChange={(e) => setThresholds({ ...thresholds, thresholdInfo: parseInt(e.target.value) || 20 })}
              inputProps={{ min: 0, max: 100 }}
              fullWidth
            />
            <TextField
              label="Umbral Warning (%)"
              type="number"
              value={thresholds.thresholdWarning}
              onChange={(e) => setThresholds({ ...thresholds, thresholdWarning: parseInt(e.target.value) || 40 })}
              inputProps={{ min: 0, max: 100 }}
              fullWidth
            />
            <TextField
              label="Umbral Error 1 (%)"
              type="number"
              value={thresholds.thresholdError}
              onChange={(e) => setThresholds({ ...thresholds, thresholdError: parseInt(e.target.value) || 60 })}
              inputProps={{ min: 0, max: 100 }}
              fullWidth
            />
            <TextField
              label="Umbral Error 2 (%)"
              type="number"
              value={thresholds.thresholdError2}
              onChange={(e) => setThresholds({ ...thresholds, thresholdError2: parseInt(e.target.value) || 80 })}
              inputProps={{ min: 0, max: 100 }}
              fullWidth
            />
            <TextField
              label="Umbral Critical (%)"
              type="number"
              value={thresholds.thresholdCritical}
              onChange={(e) => setThresholds({ ...thresholds, thresholdCritical: parseInt(e.target.value) || 95 })}
              inputProps={{ min: 0, max: 100 }}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setThresholdsDialogOpen(false)}>
            Cancelar
          </Button>
          <Button onClick={saveThresholds} color="primary" variant="contained">
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ ÚLTIMA ACTUALIZACIÓN */}
      <Box textAlign="center" marginTop={2} display="flex" justifyContent="center" alignItems="center" gap={2} flexWrap="wrap">
        <Typography variant="caption" color="textSecondary">
          Auto-actualización cada 5 segundos
        </Typography>
      </Box>
    </Box>
  );
};

export default ConnectionsDashboard;
